import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { Link } from "react-router-dom";
import { Hospital } from "@/types/map";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScenarioCard, type ScenarioCardData, type CardTheme } from "@/components/ScenarioCard";
import { useHospitalScenarios, type HospitalScenarioRow } from "@/hooks/useHospitalScenarios";
import { SCENARIO_CATEGORIES } from "@/config/scenarioCategories";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Stethoscope, Search, ArrowUpDown, ChevronRight, ExternalLink,
  Flame, Bone, Activity, Scissors, Baby, Heart, Ribbon,
  Shield, Pill, Wind, Dna, Replace,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useBrand } from "@/config/brands";
import { useAhpHeaderOffset } from "@/components/AhpGlobalHeader";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";

/** Icons for scenario categories */
const CATEGORY_ICONS: Record<string, any> = {
  emergency: Flame,
  orthopedic: Bone,
  spine: Activity,
  surgery: Scissors,
  'womens-health': Baby,
  cardiology: Heart,
  cancer: Ribbon,
  immune: Shield,
  diabetes: Pill,
  pulmonology: Wind,
  transplant: Replace,
  'gene-therapy': Dna,
};

interface MapBottomPanelProps {
  selectedHospital: Hospital | null;
  isProceduresExpanded: boolean;
  setIsProceduresExpanded: (v: boolean) => void;
  procedureCategory: string;
  setProcedureCategory: (v: string) => void;
  priceFilter: 'all' | 'gouging';
  setPriceFilter: (v: 'all' | 'gouging') => void;
  debugToggles: { bottomCarousel: boolean };
  activeScenarioId: string | null;
  scenarioInfo: { name: string; nationalAvgPrice: number; totalCodes: number; hospitalCount: number } | null;
}

/** Card theme — same as homepage light cards */
const MAP_CARD_THEME: CardTheme = {
  cardBg: '#ffffff',
  border: 'rgba(var(--civic-ink-rgb),0.12)',
  shadow: 'rgba(0,0,0,0.08)',
  red: 'var(--civic-red)',
  gold: 'var(--civic-gold)',
  green: 'var(--civic-money)',
  blue: 'var(--civic-navy)',
  text: 'var(--civic-ink)',
  textMuted: 'rgba(var(--civic-ink-rgb),0.62)',
};

/** Convert HospitalScenarioRow → ScenarioCardData for the real ScenarioCard */
function toCardData(row: HospitalScenarioRow): ScenarioCardData {
  const s = row.scenario;
  return {
    id: row.scenario_id,
    name: s.name,
    description: s.description || '',
    icon_name: s.icon_name || 'Activity',
    color: 'var(--civic-navy)',
    duration: s.duration || '',
    total_procedures: s.total_procedures || 0,
    phases: s.phases || [],
    severity_level: s.severity_level,
    calculated_avg_cost: row.nationalCost || s.estimated_cost || 0,
    phaseBreakdown: s.phaseBreakdown || {},
    phaseProcedures: {},
    tags: s.tags || [],
    created_by: null,
    is_verified: true,
  };
}

function toHospitalOverride(row: HospitalScenarioRow, hospitalId?: string) {
  return {
    blendedPrice: row.blendedPrice,
    blendedMarkup: row.blendedMarkup,
    matchedCodes: row.matched_codes,
    totalCodes: row.total_codes,
    nationalCost: row.nationalCost,
    hospitalId,
  };
}

export function MapBottomPanel({
  selectedHospital,
  isProceduresExpanded,
  setIsProceduresExpanded,
  procedureCategory,
  setProcedureCategory,
  priceFilter,
  setPriceFilter,
  debugToggles,
  activeScenarioId,
  scenarioInfo,
}: MapBottomPanelProps) {
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const ahpHeaderOffset = useAhpHeaderOffset();
  const { t } = useTranslation('map');
  const mapHeight = isAhp ? `calc(100vh - ${ahpHeaderOffset}px)` : '100vh';
  const [showAllModal, setShowAllModal] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [sortMode, setSortMode] = useState<'best-deals' | 'most-expensive'>('best-deals');

  const { cheapestAll, mostExpensiveAll, scenarios, byCategory, totalCount, isLoading: scenariosLoading } =
    useHospitalScenarios(selectedHospital?.id);

  // Build display rows: category filter + sort mode are independent
  const filteredRows = useMemo(() => {
    let rows = categoryFilter
      ? (byCategory[categoryFilter] || [])
      : scenarios;
    // The uncategorized labels make superlative PROMISES ("Most overpriced…" /
    // "Best value…"), so those lists must only contain rows matching the
    // promise — an all-cheap hospital was showing below-average scenarios
    // under "Most overpriced". Category mode's label is sort-order phrasing
    // ("worst value first"), so sorting alone is honest there.
    if (!categoryFilter) {
      rows = rows.filter(r => sortMode === 'best-deals' ? r.blendedMarkup < 0 : r.blendedMarkup > 0);
    }
    // Sort by markup % (best value = lowest markup, worst value = highest markup)
    rows = [...rows].sort((a, b) =>
      sortMode === 'best-deals'
        ? a.blendedMarkup - b.blendedMarkup
        : b.blendedMarkup - a.blendedMarkup
    );
    return rows;
  }, [categoryFilter, byCategory, scenarios, sortMode]);

  // Auto-select scenario tab when a scenario becomes active
  useEffect(() => {
    if (activeScenarioId && selectedHospital) {
      setProcedureCategory('scenario');
    }
  }, [activeScenarioId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Default to 'best-deals' when hospital selected
  useEffect(() => {
    if (selectedHospital && !activeScenarioId) {
      setProcedureCategory('best-deals');
      setCategoryFilter(null);
    }
  }, [selectedHospital?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Fluid drag handle ─────────────────────────────────────
  const panelRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef(0);
  const dragStartTop = useRef(0);
  const isDragging = useRef(false);
  const [panelTop, setPanelTop] = useState<number | null>(null);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    isDragging.current = true;
    dragStartY.current = e.clientY;
    const rect = panelRef.current?.getBoundingClientRect();
    dragStartTop.current = rect ? rect.top : 0;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current) return;
    const delta = e.clientY - dragStartY.current;
    const newTop = dragStartTop.current + delta;
    // Clamp between 80px from top and 200px from bottom
    const minTop = 80;
    const maxTop = window.innerHeight - 200;
    setPanelTop(Math.max(minTop, Math.min(maxTop, newTop)));
  }, []);

  const onPointerUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  // Compute the top position
  const defaultTop = `calc(${mapHeight} - 580px)`;
  const topStyle = isProceduresExpanded
    ? undefined
    : (panelTop !== null ? `${panelTop}px` : defaultTop);

  return (
    <div
      ref={panelRef}
      className={`absolute hidden lg:block ${
        isProceduresExpanded
          ? 'left-[405px] right-[615px] top-[20px] bottom-0 z-[100]'
          : 'left-[405px] right-[615px] z-[1]'
      }`}
      style={{
        ...(isProceduresExpanded ? {} : { top: topStyle, bottom: '0px' }),
        display: debugToggles.bottomCarousel ? '' : 'none',
        transition: isDragging.current ? 'none' : 'top 0.3s ease',
      }}
    >
      <div className="h-full bg-background/95 backdrop-blur-sm border border-border rounded-t-lg shadow-lg animate-fade-in overflow-hidden pb-[100px]">
        {/* Drag handle — fluid pointer drag */}
        <div
          className="mx-auto mt-3 mb-1 h-1.5 w-[80px] rounded-full bg-muted/80 cursor-grab active:cursor-grabbing hover:bg-muted touch-none"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        />
        <div className="h-full flex flex-col">
          <div className="px-4 py-1.5 border-b border-border/50 flex-shrink-0">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground truncate">
                {selectedHospital
                  ? `Scenario Pricing - ${selectedHospital.name}`
                  : 'Scenario Pricing'
                }
              </h3>
              {selectedHospital ? (
                <div className="flex gap-1 shrink-0 ml-2">
                  {totalCount > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs px-2 py-1 h-6 gap-1"
                      onClick={() => setShowAllModal(true)}
                    >
                      See All {totalCount}
                      <ChevronRight className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              ) : (
                <Badge variant="secondary" className="text-xs shrink-0 ml-2">
                  Select Hospital
                </Badge>
              )}
            </div>
            {selectedHospital && (
              <div className="flex items-center gap-1 mt-1.5 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                {/* Best Deals / Most Expensive / Hospital Profile */}
                <Button
                  size="sm"
                  variant={sortMode === 'best-deals' ? "default" : "ghost"}
                  className="text-xs px-2 py-0.5 h-9 md:h-7 shrink-0"
                  style={sortMode === 'best-deals' ? { backgroundColor: '#16a34a', color: '#fff' } : { color: '#16a34a' }}
                  onClick={() => { setSortMode('best-deals'); setProcedureCategory('best-deals'); }}
                >
                  Best Deals
                </Button>
                <Button
                  size="sm"
                  variant={sortMode === 'most-expensive' ? "default" : "ghost"}
                  className="text-xs px-2 py-0.5 h-9 md:h-7 shrink-0"
                  style={sortMode === 'most-expensive' ? { backgroundColor: '#dc2626', color: '#fff' } : { color: '#dc2626' }}
                  onClick={() => { setSortMode('most-expensive'); setProcedureCategory('most-expensive'); }}
                >
                  Most Expensive
                </Button>
                <div className="w-px h-4 bg-border/50 mx-0.5 shrink-0" />
                {/* Category filter chips */}
                {SCENARIO_CATEGORIES.filter(c => c.key !== 'all' && byCategory[c.key]?.length > 0).map((cat) => {
                  const Icon = CATEGORY_ICONS[cat.key] || Activity;
                  const isActive = categoryFilter === cat.key;
                  return (
                    <Button
                      key={cat.key}
                      size="sm"
                      variant={isActive ? "default" : "ghost"}
                      className="text-xs px-1.5 py-0.5 h-9 md:h-7 gap-1 shrink-0"
                      style={isActive ? { backgroundColor: cat.color, color: '#fff' } : {}}
                      onClick={() => {
                        setCategoryFilter(isActive ? null : cat.key);
                      }}
                      title={cat.browseLabel}
                    >
                      <Icon className="w-3 h-3" />
                      {cat.label}
                    </Button>
                  );
                })}
                <div className="w-px h-4 bg-border/50 mx-0.5 shrink-0" />
                {/* Hospital profile link */}
                <Link to={`/hospital/${selectedHospital.id}`}>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-xs px-2 py-0.5 h-9 md:h-7 gap-1 shrink-0"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Full Profile
                  </Button>
                </Link>
                {activeScenarioId && scenarioInfo && (
                  <>
                    <div className="w-px h-4 bg-border/50 mx-0.5 shrink-0" />
                    <Button
                      size="sm"
                      variant={procedureCategory === 'scenario' ? "default" : "ghost"}
                      className="text-xs px-2 py-0.5 h-9 md:h-7 gap-1 shrink-0"
                      onClick={() => { setProcedureCategory('scenario'); setCategoryFilter(null); }}
                    >
                      <Stethoscope className="w-3 h-3" />
                      Active Scenario
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="flex-1 overflow-hidden">
            {selectedHospital && procedureCategory === 'scenario' && activeScenarioId ? (
              <ScenarioProceduresCarousel
                hospitalId={selectedHospital.id}
                scenarioId={activeScenarioId}
                scenarioName={scenarioInfo?.name || ''}
              />
            ) : selectedHospital ? (
              <AutoScrollCarousel
                rows={filteredRows}
                label={
                  categoryFilter
                    ? `${SCENARIO_CATEGORIES.find(c => c.key === categoryFilter)?.browseLabel || 'Category'} — ${sortMode === 'best-deals' ? 'best value first' : 'worst value first'}`
                    : sortMode === 'best-deals' ? 'Best value scenarios at this hospital' : 'Most overpriced scenarios at this hospital'
                }
                isLoading={scenariosLoading}
                hospitalId={selectedHospital.id}
                priceContext={!categoryFilter ? (sortMode === 'best-deals' ? 'deal' : 'expensive') : undefined}
                isExpanded={isProceduresExpanded}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                <div className="text-center space-y-2">
                  <div className="text-4xl">{'\uD83C\uDFE5'}</div>
                  <p className="text-sm">Click on any hospital marker</p>
                  <p className="text-xs">to view scenario pricing</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* "See All Scenarios" Modal */}
      {selectedHospital && (
        <AllScenariosModal
          open={showAllModal}
          onOpenChange={setShowAllModal}
          hospitalName={selectedHospital.name}
          hospitalId={selectedHospital.id}
          byCategory={byCategory}
          totalCount={totalCount}
        />
      )}
    </div>
  );
}

// ─── Expanded Vertical Grid with Pagination ─────────────────────────
function ExpandedGrid({ rows, hospitalId, priceContext, label, pageSize, totalPages }: {
  rows: HospitalScenarioRow[];
  hospitalId?: string;
  priceContext?: 'expensive' | 'deal';
  label: string;
  pageSize: number;
  totalPages: number;
}) {
  const [page, setPage] = useState(0);
  const pageRows = rows.slice(page * pageSize, (page + 1) * pageSize);

  return (
    <div className="h-full flex flex-col">
      <div className="px-4 pt-2 pb-1 flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{label} ({rows.length})</span>
        {totalPages > 1 && (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPage(p => (p - 1 + totalPages) % totalPages)}
              className="w-6 h-6 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground bg-muted/30 hover:bg-muted/60"
            >
              <ChevronRight className="w-3.5 h-3.5 rotate-180" />
            </button>
            <span className="text-xs text-muted-foreground tabular-nums">
              {page + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => (p + 1) % totalPages)}
              className="w-6 h-6 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground bg-muted/30 hover:bg-muted/60"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-1">
        <div className="grid grid-cols-2 gap-3">
          {pageRows.map((row) => (
            <ScenarioCard
              key={row.scenario_id}
              scenario={toCardData(row)}
              compact
              isStarred={false}
              theme={MAP_CARD_THEME}
              onToggleBookmark={() => {}}
              hospitalOverride={{ ...toHospitalOverride(row, hospitalId), priceContext }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Auto-scrolling Carousel with Pagination ────────────────────────
function AutoScrollCarousel({ rows, label, isLoading, hospitalId, priceContext, isExpanded }: {
  rows: HospitalScenarioRow[];
  label: string;
  isLoading: boolean;
  hospitalId?: string;
  priceContext?: 'expensive' | 'deal';
  isExpanded?: boolean;
}) {
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Stethoscope className="h-6 w-6 mx-auto mb-2 text-muted-foreground/40" />
          <div className="text-sm text-muted-foreground">No scenario pricing data available</div>
        </div>
      </div>
    );
  }

  if (isExpanded) {
    return <ExpandedGrid rows={rows} hospitalId={hospitalId} priceContext={priceContext} label={label} pageSize={12} totalPages={Math.ceil(rows.length / 12)} />;
  }

  return <CollapsedCarousel rows={rows} hospitalId={hospitalId} priceContext={priceContext} label={label} />;
}

// ─── Collapsed Horizontal Carousel (paginated, 15 per page) ─────────
function CollapsedCarousel({ rows, hospitalId, priceContext, label }: {
  rows: HospitalScenarioRow[];
  hospitalId?: string;
  priceContext?: 'expensive' | 'deal';
  label: string;
}) {
  const PAGE_SIZE = 15;
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(rows.length / PAGE_SIZE);
  const pageRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: pageRows.length > 2, dragFree: true, align: 'start', slidesToScroll: 1 },
    [Autoplay({ delay: 4000, stopOnInteraction: true, stopOnMouseEnter: true })]
  );

  // Reset embla when page changes
  useEffect(() => {
    emblaApi?.scrollTo(0, true);
  }, [page, emblaApi]);

  return (
    <div className="h-full flex flex-col">
      <div className="px-4 pt-2 pb-1 flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{label} ({rows.length})</span>
      </div>
      <div className="flex-1 overflow-hidden px-4 py-1">
        <div ref={emblaRef} className="overflow-hidden h-full">
          <div className="flex gap-3 h-full">
            {pageRows.map((row) => (
              <div key={row.scenario_id} className="flex-[0_0_340px] min-w-0">
                <ScenarioCard
                  scenario={toCardData(row)}
                  compact
                  isStarred={false}
                  theme={MAP_CARD_THEME}
                  onToggleBookmark={() => {}}
                  hospitalOverride={{ ...toHospitalOverride(row, hospitalId), priceContext }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Page navigation */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 px-4 py-1.5 shrink-0">
          <button
            onClick={() => setPage(p => (p - 1 + totalPages) % totalPages)}
            className="w-6 h-6 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground bg-muted/30 hover:bg-muted/60"
          >
            <ChevronRight className="w-3.5 h-3.5 rotate-180" />
          </button>
          <span className="text-xs text-muted-foreground tabular-nums min-w-[60px] text-center">
            Page {page + 1} / {totalPages}
          </span>
          <button
            onClick={() => setPage(p => (p + 1) % totalPages)}
            className="w-6 h-6 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground bg-muted/30 hover:bg-muted/60"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── All Scenarios Modal ────────────────────────────────────────────
function AllScenariosModal({ open, onOpenChange, hospitalName, hospitalId, byCategory, totalCount }: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  hospitalName: string;
  hospitalId: string;
  byCategory: Record<string, HospitalScenarioRow[]>;
  totalCount: number;
}) {
  const [search, setSearch] = useState('');
  const [sortAsc, setSortAsc] = useState(true);

  const categoryEntries = SCENARIO_CATEGORIES
    .filter((cat) => cat.key !== 'all' && byCategory[cat.key]?.length > 0)
    .map((cat) => {
      let rows = byCategory[cat.key] || [];
      if (search) {
        const q = search.toLowerCase();
        rows = rows.filter((r) =>
          r.scenario.name.toLowerCase().includes(q) ||
          (r.scenario.category || '').toLowerCase().includes(q)
        );
      }
      if (!sortAsc) rows = [...rows].reverse();
      return { cat, rows };
    })
    .filter(({ rows }) => rows.length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-lg">
            All Scenarios - {hospitalName}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">{totalCount} scenarios with pricing data</p>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="Search scenarios..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-8 text-xs"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSortAsc(!sortAsc)}
            className="h-8 text-xs gap-1 shrink-0"
          >
            <ArrowUpDown className="w-3 h-3" />
            {sortAsc ? 'Cheapest First' : 'Most Expensive First'}
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-6 pr-1">
          {categoryEntries.map(({ cat, rows }) => (
            <div key={cat.key}>
              <div className="flex items-center gap-2 mb-3 sticky top-0 bg-background/95 backdrop-blur-sm py-1 z-10">
                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: cat.color }} />
                <h3 className="text-sm font-semibold text-foreground">{cat.browseLabel}</h3>
                <span className="text-xs text-muted-foreground">({rows.length})</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {rows.map((row) => (
                  <ScenarioCard
                    key={row.scenario_id}
                    scenario={toCardData(row)}
                    compact
                    isStarred={false}
                    theme={MAP_CARD_THEME}
                    onToggleBookmark={() => {}}
                    hospitalOverride={toHospitalOverride(row, hospitalId)}
                  />
                ))}
              </div>
            </div>
          ))}
          {categoryEntries.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              {search ? 'No scenarios match your search' : 'No scenario data available'}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Scenario Procedures Carousel (existing, for active scenario drill-down) ───
interface ScenarioProceduresCarouselProps {
  hospitalId: string;
  scenarioId: string;
  scenarioName: string;
}

function ScenarioProceduresCarousel({ hospitalId, scenarioId, scenarioName }: ScenarioProceduresCarouselProps) {
  const { data: procedures = [], isLoading } = useQuery({
    queryKey: ['scenario-procedures-carousel', scenarioId, hospitalId],
    queryFn: async () => {
      // scenarios has no `procedures` column — the rows live in
      // scenario_procedures (this carousel silently returned [] before).
      const { data: procRows } = await supabase
        .from('scenario_procedures')
        .select('code, code_type, description, phase, quantity')
        .eq('scenario_id', scenarioId);
      if (!procRows?.length) return [];

      const procs = procRows.map(r => ({ code: r.code, code_type: r.code_type, description: r.description, phase: r.phase, quantity: r.quantity }));
      const cptCodes = procs
        .filter((p: any) => p.code_type === 'CPT' || p.codeType === 'CPT')
        .map((p: any) => p.code);

      if (cptCodes.length === 0) return procs.map((p: any) => ({ ...p, hospital_price: null, national_avg: null, markup: null }));

      const { data: analytics } = await supabase
        .from('hospital_procedure_analytics')
        .select('procedure_code, hospital_gross_charge, national_avg_gross, gross_markup_percentage, procedure_description')
        .eq('hospital_id', hospitalId)
        .in('procedure_code', cptCodes);

      const analyticsMap = new Map((analytics || []).map((a: any) => [a.procedure_code, a]));

      return procs.map((p: any) => {
        const a = analyticsMap.get(p.code);
        return {
          code: p.code,
          code_type: p.code_type || p.codeType || 'CPT',
          description: p.description || a?.procedure_description || p.code,
          phase: p.phase || '',
          quantity: p.quantity || 1,
          hospital_price: a?.hospital_gross_charge || null,
          national_avg: a?.national_avg_gross || null,
          markup: a?.gross_markup_percentage || null,
          setting: p.setting || '',
        };
      });
    },
    enabled: !!scenarioId && !!hospitalId,
  });

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-sm text-muted-foreground">Loading scenario procedures...</div>
      </div>
    );
  }

  if (procedures.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Stethoscope className="h-6 w-6 mx-auto mb-2 text-muted-foreground/40" />
          <div className="text-sm text-muted-foreground">No procedure data for this scenario</div>
        </div>
      </div>
    );
  }

  const getMarkupColor = (markup: number | null) => {
    if (markup == null) return 'text-muted-foreground';
    if (markup > 100) return 'text-red-500';
    if (markup > 50) return 'text-orange-500';
    if (markup > 0) return 'text-yellow-500';
    return 'text-green-500';
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-x-auto px-4 py-2">
        <div className="flex gap-3" style={{ minWidth: `${procedures.length * 220}px` }}>
          {procedures.map((proc: any, i: number) => (
            <div
              key={`${proc.code}-${i}`}
              className="w-[200px] shrink-0 p-3 rounded-lg border bg-card/80 border-border/50"
            >
              <div className="flex items-center gap-1.5 mb-2">
                <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0">
                  {proc.code}
                </Badge>
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                  {proc.code_type}
                </Badge>
              </div>
              <div className="text-xs font-medium text-foreground truncate mb-1" title={proc.description}>
                {proc.description}
              </div>
              {proc.phase && (
                <div className="text-[10px] text-muted-foreground mb-2">{proc.phase}</div>
              )}
              <div className="space-y-1">
                {proc.hospital_price != null ? (
                  <>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Hospital</span>
                      <span className="font-mono font-semibold">${proc.hospital_price.toLocaleString()}</span>
                    </div>
                    {proc.national_avg != null && (
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">National</span>
                        <span className="font-mono text-muted-foreground">${proc.national_avg.toLocaleString()}</span>
                      </div>
                    )}
                    {proc.markup != null && (
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Markup</span>
                        <span className={`font-semibold ${getMarkupColor(proc.markup)}`}>
                          {proc.markup > 0 ? '+' : ''}{proc.markup.toFixed(0)}%
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-[10px] text-muted-foreground text-center py-1">No pricing data</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default MapBottomPanel;
